import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Info, CircleCheck, TriangleAlert, CircleX, Lightbulb } from "lucide-react";
import { cn } from "../../lib/cn";
import { Markdown } from "../Markdown";

/**
 * Callout - a titled, semantic banner for caveats and notices. Renderer for the
 * K2 `callout` block (`docs/contracts/block-registry.md`): the platform's chat
 * `BlockRenderer` passes a `callout` block's `props` straight through, so the
 * prop names match the block payload exactly.
 *
 * Block props (`{ variant, title?, body }`):
 *   - `variant` - semantic tone: `"info"` | `"success"` | `"warning"` |
 *     `"error"` | `"tip"`. Drives the icon + color. Defaults to `"info"`.
 *   - `title?` - optional bold heading line.
 *   - `body` - the message. Rendered as markdown (links, emphasis, lists).
 *     Required.
 *
 * Client-only props (never part of the streamed block): `className`, `icon`
 * (override the default per-variant icon).
 *
 * Maps onto the same banner anatomy as the stock `Alert` primitive but is
 * variant-driven by the five semantic tones the model emits, with the body
 * rendered through the shared `Markdown` primitive so a caveat can carry a link
 * or a short list. Theming is entirely token-driven (`bg-info/5`,
 * `text-success`, `border-warning/30`, ...) so per-studio retint + dark mode
 * work with no hardcoded colors. SSR-safe: no `window` / `document` access.
 */

/** Source: prompt-kit Callout, AI Elements. */
export type CalloutVariant = "info" | "success" | "warning" | "error" | "tip";

const calloutVariants = cva(
  cn(
    // Tinted-banner variant of the shared block surface: same radius + inset as
    // BLOCK_SURFACE, but the semantic border/fill below replace the card frame.
    "flex items-start gap-3 rounded-2xl border p-4 text-sm sm:p-5",
    "[&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2",
  ),
  {
    variants: {
      variant: {
        info: "border-info/30 bg-info/5",
        success: "border-success/30 bg-success/5",
        warning: "border-warning/30 bg-warning/5",
        error: "border-destructive/30 bg-destructive/5",
        tip: "border-accent/30 bg-accent/5",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

const ICONS: Record<CalloutVariant, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleX,
  tip: Lightbulb,
};

const ICON_TONE: Record<CalloutVariant, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  error: "text-destructive",
  tip: "text-accent",
};

/** Screen-reader prefix announcing the tone, so the banner reads in context. */
const VARIANT_LABEL: Record<CalloutVariant, string> = {
  info: "Note",
  success: "Success",
  warning: "Warning",
  error: "Error",
  tip: "Tip",
};

export interface CalloutProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof calloutVariants> {
  /** Optional bold heading line. */
  title?: string;
  /** The message body. Rendered as markdown. Required. */
  body: string;
  /** Override the default per-variant icon. Pass `null` to hide it. */
  icon?: React.ReactNode;
}

export const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(function Callout(
  { className, variant, title, body, icon, ...rest },
  ref,
) {
  const resolved: CalloutVariant = variant ?? "info";
  const Icon = ICONS[resolved];

  return (
    <div
      ref={ref}
      role="note"
      className={cn(calloutVariants({ variant }), className)}
      data-slot="callout"
      data-variant={resolved}
      {...rest}
    >
      {icon === null ? null : (
        <span className={cn("mt-0.5 shrink-0", ICON_TONE[resolved])} aria-hidden>
          {icon ?? <Icon className="size-4" />}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {title ? (
          <p className="mb-0.5 font-semibold leading-snug text-foreground">
            <span className="sr-only">{VARIANT_LABEL[resolved]}: </span>
            {title}
          </p>
        ) : (
          <span className="sr-only">{VARIANT_LABEL[resolved]}: </span>
        )}
        <div className="text-muted-foreground [&_p]:leading-relaxed [&_p:last-child]:mb-0">
          <Markdown content={body} />
        </div>
      </div>
    </div>
  );
});

Callout.displayName = "Callout";

export { calloutVariants };
export type CalloutVariantProps = VariantProps<typeof calloutVariants>;
